import React, {
  useEffect,
  useState,
} from 'react';
import styled from 'styled-components';

import {
  BackgroundElement,
  Nullable,
} from '@/core/interfaces/common';

import { colors } from '@/utils/constants';
import { hexToRgb } from '@/utils/helpers';
import { fetchAdvocatesCount } from '@/utils/requestHelpers';

interface AdvocatesCounterProps {
  backgroundColor: string;
  uuid: string | Array<string> | undefined;
}

const CounterBlock = styled.p<BackgroundElement>`
  margin: 28px 0;
  border-radius: 15px;
  padding: 10px 15px;
  background-color: ${props => hexToRgb(props.backgroundcolor || colors.white, '0.05')};
  font-weight: bold;
`;

/**
 * The AdvocatesCounter component is response for both RENDERING
 * and REQUESTING the advocates count from the API. This is
 * performed separately from the initial API call for reasons
 * having to do with caching right now. The API response for the
 * action center details are cached at both the CDN layer (for
 * about one minute) and at the application server layer (for
 * practical purposes this can be considered indefinite). So to
 * avoid futzing with caching, we simply make a second call here.
 * This value is cached too, but not nearly as aggressively.
 *
 * @param backgroundColor - hex color for the background
 * @param uuid - UUID of the action center for API call
 * @constructor
 */
export const AdvocatesCounter = ({
  backgroundColor,
  uuid,
}: AdvocatesCounterProps) => {
  const [
    advocateCount,
    setAdvocateCount,
  ] = useState<Nullable<number>>(null);

  useEffect(() => {
    if (!uuid) return;

    fetchAdvocatesCount(uuid)
      .then(response => {
        if (response?.advocates !== undefined) {
          setAdvocateCount(response.advocates);
        }
      })
      .catch(() => {
        // Silently fail, leave count as null 🙏
      });
  }, [uuid]);

  if (advocateCount === null) {
    return (<></>);
  }

  if (advocateCount < 5) {
    return (
      <CounterBlock backgroundcolor={backgroundColor}>
        Be one of our first advocates - act now!
      </CounterBlock>
    );
  }

  return (
    <CounterBlock backgroundcolor={backgroundColor}>
      Join the first
      {' '}
      {advocateCount}
      {' '}
      advocates
      !
    </CounterBlock>
  );
};
